import type React from "react"

import { useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useScene } from "@/contexts/scene-context"
import { useData } from "@/contexts/data-context"
import { Upload, FileText, Check, AlertTriangle } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

export default function ModelLoader() {
  const { sceneState, updateSceneState } = useScene()
  const { selectedEnvironment } = useData()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadStatus, setUploadStatus] = useState<"idle" | "loading" | "success" | "error">("idle")
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setUploadStatus("loading")

      // Simulate file processing
      setTimeout(() => {
        const fileName = file.name
        const fileExtension = fileName.toLowerCase().split(".").pop()

        if (fileExtension === "obj" || fileExtension === "stl") {
          updateSceneState({ loadedModel: fileName })
          setUploadStatus("success")
          setErrorMessage(null)
        } else {
          setUploadStatus("error")
          setErrorMessage("Please select a .obj or .stl file")
        }
      }, 1500)
    }
  }

  const loadSampleModel = () => {
    setUploadStatus("loading")

    // Simulate loading
    setTimeout(() => {
      let modelName = "sample_model.obj"

      switch (selectedEnvironment) {
        case "datacenter":
          modelName = "sample_datacenter.obj"
          break
        case "greenhouse":
          modelName = "sample_greenhouse.obj"
          break
        case "charging":
          modelName = "sample_charging.obj"
          break
        case "smartbuilding":
          modelName = "sample_building.obj"
          break
        case "industrial":
          modelName = "sample_factory.obj"
          break
      }

      updateSceneState({ loadedModel: modelName })
      setUploadStatus("success")
    }, 1000)
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">3D Model</h3>

      <div className="space-y-2">
        <Label htmlFor="model-upload">Load 3D Model</Label>
        <div className="flex gap-2">
          <Input
            id="model-upload"
            type="file"
            accept=".obj,.stl"
            onChange={handleFileUpload}
            ref={fileInputRef}
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            className="flex-1"
            disabled={uploadStatus === "loading"}
          >
            {uploadStatus === "loading" ? (
              <>
                <span className="animate-spin mr-2">◌</span>
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload Model
              </>
            )}
          </Button>
        </div>
      </div>

      <Button onClick={loadSampleModel} variant="outline" className="w-full" disabled={uploadStatus === "loading"}>
        <FileText className="w-4 h-4 mr-2" />
        Load Sample Model
      </Button>

      {uploadStatus === "error" && errorMessage && (
        <div className="text-sm text-destructive bg-destructive/10 p-2 rounded flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {errorMessage}
        </div>
      )}

      {sceneState.loadedModel && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{sceneState.loadedModel}</span>
              </div>
              <Badge variant="outline" className="bg-success/10 text-success">
                <Check className="w-3 h-3 mr-1" /> Loaded
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
